import React, { useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import handleKeyPress from '../../common/handleKeyPress';

/*
    <BananatagButton
        size: large, medium, or small, defaults to large,
        text: button text,
        index: needed for file upload buttons,
        file: boolean, whether it's a file upload button or not,
        type: style of button, either solid, clear, outline or warning. defaults to solid,
        styling: for any style overrides required, use the same way as React's inline styling,
        icon: name of the Material Icon if one is needed,
    />
*/

const ButtonBase = styled.button`
    align-items: center;
    border: none;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    font-size: ${({ sizing }) => sizing.fontSize};
    font-weight: 700;
    height: ${({ sizing }) => sizing.height};
    justify-content: flext-start;
    outline: none;
    padding: ${({ icon }) => (icon ? '0 16px 1px 6px' : '0 16px')};
    transition: background-color 200ms linear, box-shadow 200ms linear, color 200ms linear;
    white-space: nowrap;
    &:disabled {
        pointer-events: none;
        background: none;
        color: ${({ theme }) => theme.colors.neutral100};
    }
    &:focus {
        outline: ${({ theme }) => `solid ${theme.colors.primary300}`};
    }
`;

const SolidButton = styled(ButtonBase)`
    background-color: ${({ theme }) => theme.colors.primary300};
    color: white;
    &:hover {
        background-color: ${({ theme }) => theme.colors.primary700};
    }
    &:disabled {
        background-color: ${({ theme }) => theme.colors.neutral100};
        color: white;
    }
`;

const ClearButton = styled(ButtonBase)`
    background: none;
    color: ${({ theme }) => theme.colors.neutral700};
    &:hover {
        background-color: rgba(2, 119, 189, 0.05);
    }
    &:disabled {
        color: ${({ theme }) => theme.colors.neutral100};
    }
`;

const OutlineButton = styled(ButtonBase)`
    background: none;
    color: ${({ theme }) => theme.colors.primary300};
    box-shadow: ${({ theme }) => `inset 0px 0px 0px 1px ${theme.colors.neutral100}`};
    &:hover {
        box-shadow: ${({ theme }) => `inset 0px 0px 0px 2px ${theme.colors.neutral100}`};
        color: ${({ theme }) => theme.colors.primary700};
    }
    &:disabled {
        color: ${({ theme }) => theme.colors.neutral100};
    }
`;

const WarningButton = styled(ButtonBase)`
    background-color: ${({ theme }) => theme.colors.danger700};
    color: white;
    &:hover {
        background-color: #a90a0a;
    }
    &:active {
        background-color: #7f0000;
    }
    &:disabled {
        color: ${({ theme }) => theme.colors.neutral100};
    }
`;

const getComponent = (type) => {
    const components = {
        solid: SolidButton,
        clear: ClearButton,
        outline: OutlineButton,
        warning: WarningButton,
    };
    return components[type];
};

const getSizing = (size) => {
    const sizing = {
        small: { height: '28px', fontSize: '12px', iconSize: '18px' },
        medium: { height: '32px', fontSize: '13px', iconSize: '22px' },
        large: { height: '40px', fontSize: '16px', iconSize: '24px' },
    };
    return sizing[size];
};

const BananatagButton = (props) => {
    const {
        onClick,
        text,
        ariaLabel,
        type,
        styling,
        disabled,
        icon,
        file,
        index,
        onChange,
        size,
        allowGifs,
        multiple,
    } = props;
    const sizing = getSizing(size);
    const Component = getComponent(type);
    const ref = useRef(null);

    if (file) {
        return (
            <Component
                as="label"
                htmlFor={index}
                style={{ ...styling }}
                icon={!!icon}
                sizing={sizing}
                role="button"
                aria-label={ariaLabel || (onClick && text)}
                data-test-id={`${text}-input`}
                onKeyDown={(e) => {
                    !disabled && handleKeyPress(e, () => ref.current.click());
                }}
                tabIndex={0}
            >
                {icon && (
                    <i
                        aria-hidden
                        className="material-icons"
                        style={{ padding: 4, fontSize: sizing.iconSize, marginRight: 4 }}
                    >
                        {icon}
                    </i>
                )}
                <span style={{ fontSize: 12, fontWeight: 600 }}>{text.toUpperCase()}</span>
                <label htmlFor={index}>
                    <input
                        type="file"
                        accept={`image/png,image/jpg,image/jpeg${allowGifs ? ',image/gif' : ''}`}
                        name={index}
                        id={index}
                        onChange={onChange}
                        style={{ display: 'none' }}
                        multiple={multiple}
                        ref={ref}
                    />
                </label>
            </Component>
        );
    }

    return (
        <Component
            style={{ ...styling }}
            disabled={disabled}
            type="button"
            icon={!!icon}
            sizing={sizing}
            onClick={onClick}
            onKeyDown={(e) => {
                !disabled && handleKeyPress(e, () => onClick);
            }}
            role="button"
            aria-label={ariaLabel || (onClick && text)}
            data-test-id={`${text}-button`}
        >
            {icon && (
                <i
                    aria-hidden
                    className="material-icons"
                    style={{ padding: 4, fontSize: sizing.iconSize, marginRight: 4 }}
                >
                    {icon}
                </i>
            )}
            {text.toUpperCase()}
        </Component>
    );
};

BananatagButton.propTypes = {
    text: PropTypes.string,
    ariaLabel: PropTypes.string,
    onClick: PropTypes.func,
    onChange: PropTypes.func,
    file: PropTypes.bool,
    index: PropTypes.string,
    type: PropTypes.oneOf(['solid', 'outline', 'clear', 'warning']),
    icon: PropTypes.string,
    styling: PropTypes.object,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    allowGifs: PropTypes.bool,
    multiple: PropTypes.bool,
};

BananatagButton.defaultProps = {
    size: 'large',
    text: 'enter button text',
    ariaLabel: '',
    onClick: () => {},
    onChange: () => {},
    disabled: false,
    index: '',
    file: false,
    type: 'solid',
    styling: {},
    icon: null,
    allowGifs: false,
    multiple: false,
};

export default BananatagButton;
